<template>
  <div class="station-groups-view">
    <div class="content">
      <div class="content-toolbar">
        <div class="toolbar-left">
          <div class="page-title">
            <h1>Station Groups</h1>
          </div>
        </div>
        <div class="toolbar-right">
          <button class="btn-primary" @click="showAddModal = true">
            <AppIcon name="plus" />New Group
          </button>
        </div>
      </div>

      <div v-if="loading" class="loading">Loading groups...</div>

      <div v-else class="two-panel-layout">
        <!-- Left Panel: Groups List -->
        <div class="groups-panel">
          <div class="panel-header">
            <span class="panel-title">Groups</span>
          </div>
          <div class="groups-list">
            <!-- Ungrouped (show first if has stations) -->
            <div 
              v-if="ungroupedStations.length > 0"
              :class="['group-item', { active: selectedGroupId === 'ungrouped' }]"
              @click="selectGroup('ungrouped')"
            >
              <div class="group-color" style="background-color: #6B7280"></div>
              <div class="group-details">
                <span class="group-name">Ungrouped</span>
                <span class="group-count">{{ ungroupedStations.length }}</span>
              </div>
            </div>

            <!-- Actual groups -->
            <div 
              v-for="group in actualGroups" 
              :key="group._id"
              :class="['group-item', { active: selectedGroupId === group._id }]"
              @click="selectGroup(group._id)"
            >
              <div class="group-color" :style="{ backgroundColor: group.color || '#3B82F6' }"></div>
              <div class="group-details">
                <span class="group-name">{{ group.name }}</span>
                <span class="group-count">{{ group.stationCount }}</span>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="actualGroups.length === 0 && ungroupedStations.length === 0" class="empty-groups">
              <p>No groups yet</p>
            </div>
          </div>
        </div>

        <!-- Right Panel: Selected Group Details -->
        <div class="details-panel">
          <template v-if="selectedGroupId">
            <!-- Group Header -->
            <div class="details-header">
              <div class="details-title">
                <div class="group-color-large" :style="{ backgroundColor: selectedGroupColor }"></div>
                <div>
                  <h2>{{ selectedGroupName }}</h2>
                  <span class="station-count">{{ selectedStations.length }} station{{ selectedStations.length !== 1 ? 's' : '' }}</span>
                </div>
              </div>
              <div v-if="selectedGroupId !== 'ungrouped'" class="details-actions">
                <button class="btn-icon" @click="editGroup(selectedGroup)" title="Edit group">
                  <AppIcon name="edit" />
                </button>
                <button class="btn-icon btn-danger" @click="confirmDeleteGroup(selectedGroup)" title="Delete group">
                  <AppIcon name="trash" />
                </button>
              </div>
            </div>

            <!-- Search -->
            <div class="search-box">
              <AppIcon name="search" />
              <input 
                v-model="searchQuery" 
                type="text" 
                :placeholder="`Search in ${selectedGroupName}...`"
              >
              <button v-if="searchQuery" class="btn-clear" @click="searchQuery = ''">
                <AppIcon name="times" />
              </button>
            </div>

            <!-- Stations List -->
            <div class="stations-list">
              <div 
                v-for="station in filteredStations" 
                :key="station._id" 
                class="station-item"
              >
                <div class="station-logo-small">
                  <img v-if="getStationLogo(station)" :src="getStationLogo(station)" :alt="station.name">
                  <AppIcon v-else name="broadcast-tower" />
                </div>
                <span class="station-name">{{ station.name }}</span>
                <span :class="['status-dot', { online: station.isConnected }]"></span>
                <select 
                  class="group-select"
                  :value="station.groupId ? station.groupId.toString() : ''"
                  @change="moveStation(station, $event.target.value)"
                >
                  <option value="">Ungrouped</option>
                  <option 
                    v-for="g in actualGroups" 
                    :key="g._id" 
                    :value="g._id.toString()"
                  >
                    {{ g.name }}
                  </option>
                </select>
              </div>

              <div v-if="filteredStations.length === 0 && searchQuery" class="no-results">
                No stations match "{{ searchQuery }}"
              </div>

              <div v-if="selectedStations.length === 0 && !searchQuery" class="no-stations">
                <AppIcon name="inbox" />
                <p>No stations in this group</p>
              </div>
            </div>
          </template>

          <!-- No group selected state -->
          <div v-else class="no-selection">
            <AppIcon name="layer-group" />
            <p>Select a group to view stations</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Group Modal -->
    <div v-if="showAddModal || showEditModal" class="modal-overlay" @click.self="closeModals">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ showEditModal ? 'Edit Group' : 'New Group' }}</h2>
          <button class="modal-close" @click="closeModals">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Group Name</label>
            <input 
              v-model="formData.name" 
              type="text" 
              placeholder="e.g., California Markets"
              @keyup.enter="saveGroup"
            >
          </div>
          <div class="form-group">
            <label>Color</label>
            <div class="color-options">
              <button 
                v-for="color in colorOptions" 
                :key="color"
                :class="['color-option', { selected: formData.color === color }]"
                :style="{ backgroundColor: color }"
                @click="formData.color = color"
              ></button>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="closeModals">Cancel</button>
          <button class="btn-primary" @click="saveGroup" :disabled="!formData.name.trim()">
            {{ showEditModal ? 'Save Changes' : 'Create Group' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
      <div class="modal modal-sm">
        <div class="modal-header">
          <h2>Delete Group</h2>
          <button class="modal-close" @click="showDeleteModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete <strong>{{ groupToDelete?.name }}</strong>?</p>
          <p class="text-muted">Stations in this group will become ungrouped.</p>
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="showDeleteModal = false">Cancel</button>
          <button class="btn-danger" @click="deleteGroup">Delete Group</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authFetch } from '../services/api'

export default {
  name: 'StationGroupsView',
  
  data() {
    return {
      loading: true,
      groups: [],
      selectedGroupId: null,
      searchQuery: '',
      showAddModal: false,
      showEditModal: false,
      showDeleteModal: false,
      groupToDelete: null,
      editingGroup: null,
      formData: {
        name: '',
        color: '#3B82F6'
      },
      colorOptions: [
        '#3B82F6', // Blue
        '#10B981', // Green
        '#F59E0B', // Amber
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#EC4899', // Pink
        '#06B6D4', // Cyan
        '#6B7280'  // Gray
      ]
    }
  },

  computed: {
    actualGroups() {
      return this.groups.filter(g => !g.isVirtual)
    },
    
    ungroupedStations() {
      const ungroupedGroup = this.groups.find(g => g.isVirtual)
      return ungroupedGroup?.stations || []
    },
    
    selectedGroup() {
      if (this.selectedGroupId === 'ungrouped') {
        return { name: 'Ungrouped', color: '#6B7280', isVirtual: true }
      }
      return this.actualGroups.find(g => g._id === this.selectedGroupId)
    },
    
    selectedGroupName() {
      return this.selectedGroup?.name || ''
    },
    
    selectedGroupColor() {
      return this.selectedGroup?.color || '#6B7280'
    },
    
    selectedStations() {
      if (this.selectedGroupId === 'ungrouped') {
        return this.ungroupedStations
      }
      const group = this.groups.find(g => g._id === this.selectedGroupId)
      return group?.stations || []
    },
    
    filteredStations() {
      if (!this.searchQuery.trim()) {
        return this.selectedStations
      }
      const query = this.searchQuery.toLowerCase()
      return this.selectedStations.filter(s => 
        s.name.toLowerCase().includes(query)
      )
    }
  },

  async mounted() {
    await this.loadGroups()
  },

  methods: {
    getStationLogo(station) {
      return station.logoUrl || station.logo || null
    },
    async loadGroups() {
      this.loading = true
      try {
        const response = await authFetch('/api/station-groups?includeStations=true')
        if (response.ok) {
          this.groups = await response.json()
          
          // Auto-select first group
          if (!this.selectedGroupId) {
            if (this.ungroupedStations.length > 0) {
              this.selectedGroupId = 'ungrouped'
            } else if (this.actualGroups.length > 0) {
              this.selectedGroupId = this.actualGroups[0]._id
            }
          }
        }
      } catch (err) {
        console.error('Failed to load groups:', err)
      } finally {
        this.loading = false
      }
    },

    selectGroup(groupId) {
      this.selectedGroupId = groupId
      this.searchQuery = ''
    },

    editGroup(group) {
      this.editingGroup = group
      this.formData = {
        name: group.name,
        color: group.color || '#3B82F6'
      }
      this.showEditModal = true
    },

    confirmDeleteGroup(group) {
      this.groupToDelete = group
      this.showDeleteModal = true
    },

    closeModals() {
      this.showAddModal = false
      this.showEditModal = false
      this.editingGroup = null
      this.formData = { name: '', color: '#3B82F6' }
    },

    async saveGroup() {
      if (!this.formData.name.trim()) return

      try {
        let response
        if (this.showEditModal && this.editingGroup) {
          response = await authFetch(`/api/station-groups/${this.editingGroup._id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(this.formData)
          })
        } else {
          response = await authFetch('/api/station-groups', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(this.formData)
          })
        }
        
        if (!response.ok) {
          const err = await response.json()
          throw new Error(err.error || 'Failed to save group')
        }
        
        this.closeModals()
        await this.loadGroups()
      } catch (err) {
        console.error('Failed to save group:', err)
        alert(err.message || 'Failed to save group')
      }
    },

    async deleteGroup() {
      if (!this.groupToDelete) return

      try {
        const response = await authFetch(`/api/station-groups/${this.groupToDelete._id}`, {
          method: 'DELETE'
        })
        
        if (!response.ok) {
          const err = await response.json()
          throw new Error(err.error || 'Failed to delete group')
        }
        
        // If we deleted the selected group, select ungrouped or first group
        if (this.selectedGroupId === this.groupToDelete._id) {
          this.selectedGroupId = null
        }
        
        this.showDeleteModal = false
        this.groupToDelete = null
        await this.loadGroups()
      } catch (err) {
        console.error('Failed to delete group:', err)
        alert(err.message || 'Failed to delete group')
      }
    },

    async moveStation(station, newGroupId) {
      try {
        const response = await authFetch(`/api/stations/${station._id}/group`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ groupId: newGroupId || null })
        })
        
        if (!response.ok) {
          const err = await response.json()
          throw new Error(err.error || 'Failed to move station')
        }
        
        await this.loadGroups()
      } catch (err) {
        console.error('Failed to move station:', err)
        alert(err.message || 'Failed to move station')
      }
    }
  }
}
</script>

<style scoped>
.station-groups-view {
  min-height: calc(100vh - 74px);
  background: var(--gray-50);
}

.content {
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
}

.content-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title h1 {
  font-size: 18px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0;
}

.loading {
  text-align: center;
  padding: 60px 20px;
  color: var(--gray-500);
}

/* Two Panel Layout */
.two-panel-layout {
  display: flex;
  gap: 24px;
  min-height: 500px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Left Panel */
.groups-panel {
  width: 280px;
  flex-shrink: 0;
  background: var(--white);
  border-radius: 8px;
  border: 1px solid var(--gray-200);
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.panel-header {
  padding: 16px;
  border-bottom: 1px solid var(--gray-100);
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.groups-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.group-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
}

.group-item:hover {
  background: var(--gray-50);
}

.group-item.active {
  background: var(--gray-100);
}

.group-color {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  flex-shrink: 0;
}

.group-details {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.group-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--gray-800);
}

.group-count {
  font-size: 12px;
  color: var(--gray-500);
  background: var(--gray-100);
  padding: 2px 8px;
  border-radius: 10px;
}

.group-item.active .group-count {
  background: var(--gray-200);
}

.empty-groups {
  padding: 20px;
  text-align: center;
  color: var(--gray-400);
  font-size: 13px;
}

/* Right Panel */
.details-panel {
  flex: 1;
  background: var(--white);
  border-radius: 8px;
  border: 1px solid var(--gray-200);
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--gray-100);
}

.details-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.group-color-large {
  width: 4px;
  height: 40px;
  border-radius: 4px;
}

.details-title h2 {
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0;
}

.details-title .station-count {
  font-size: 12px;
  color: var(--gray-500);
}

.details-actions {
  display: flex;
  gap: 8px;
}

/* Search Box */
.search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--gray-100);
  background: var(--gray-50);
  height: 48px;
  box-sizing: border-box;
}

.search-box svg {
  color: var(--gray-400);
  font-size: 14px;
  flex-shrink: 0;
}

.search-box input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  color: var(--gray-700);
  outline: none;
  min-width: 0;
}

.search-box input::placeholder {
  color: var(--gray-400);
}

.btn-clear {
  background: none;
  border: none;
  color: var(--gray-400);
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-clear:hover {
  color: var(--gray-600);
}

/* Stations List */
.stations-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.station-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 6px;
  transition: background 0.15s;
}

.station-item:hover {
  background: var(--gray-50);
}

.station-logo-small {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: var(--gray-100);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.station-logo-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.station-logo-small svg {
  font-size: 12px;
  color: var(--gray-400);
}

.station-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--gray-700);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gray-300);
  flex-shrink: 0;
}

.status-dot.online {
  background: #10B981;
}

.group-select {
  padding: 6px 32px 6px 12px;
  font-size: 13px;
  font-family: 'Poppins', sans-serif;
  border: 1px solid var(--gray-300);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-700);
  cursor: pointer;
  min-width: 130px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M2.5 4.5L6 8l3.5-3.5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  transition: all 0.15s;
}

.group-select:hover {
  border-color: var(--gray-400);
}

.group-select:focus {
  outline: none;
  border-color: var(--gray-400);
}

.no-results, .no-stations {
  padding: 40px 20px;
  text-align: center;
  color: var(--gray-400);
  font-size: 14px;
}

.no-stations svg {
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.no-stations p {
  margin: 0;
}

.no-selection {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--gray-400);
}

.no-selection i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.no-selection p {
  font-size: 14px;
  margin: 0;
}

/* Buttons */
.btn-icon {
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-500);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  padding: 0;
  font-size: 14px;
}

.btn-icon:hover {
  background: var(--gray-50);
  color: var(--gray-700);
}

.btn-icon.btn-danger {
  border: none;
  background: #DC2626;
  color: var(--white);
}

.btn-icon.btn-danger:hover {
  background: #B91C1C;
  color: var(--white);
}

.btn-primary {
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  background: var(--accent);
  color: var(--white);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}

.btn-primary:hover {
  background: var(--accent-hover);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-secondary:hover {
  background: var(--gray-50);
}

.btn-danger {
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  background: #DC2626;
  color: var(--white);
  cursor: pointer;
}

.btn-danger:hover {
  background: #B91C1C;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--white);
  border-radius: 8px;
  width: 100%;
  max-width: 440px;
  box-shadow: var(--shadow-lg);
}

.modal.modal-sm {
  max-width: 380px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--gray-200);
}

.modal-header h2 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--gray-400);
  cursor: pointer;
  line-height: 1;
}

.modal-close:hover {
  color: var(--gray-600);
}

.modal-body {
  padding: 20px;
}

.modal-body p {
  margin: 0 0 12px 0;
  color: var(--gray-600);
}

.text-muted {
  font-size: 13px;
  color: var(--gray-500);
}

.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-700);
  margin-bottom: 6px;
}

.form-group input {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  outline: none;
  transition: border-color 0.15s;
}

.form-group input:focus {
  border-color: var(--accent);
}

.color-options {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.color-option {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
}

.color-option:hover {
  transform: scale(1.1);
}

.color-option.selected {
  border-color: var(--gray-900);
  box-shadow: 0 0 0 2px var(--white), 0 0 0 4px var(--gray-900);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid var(--gray-200);
}
</style>
